import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../services/api";
import {
  MdSchedule,
  MdArrowBack,
  MdEdit,
  MdPerson,
  MdPeople,
  MdEvent,
  MdFlag,
  MdLink,
  MdLabel,
  MdInfo,
  MdAutorenew,
  MdCalendarToday,
  MdCheckCircle,
  MdAccessTime,
} from "react-icons/md";
import { format, formatDistanceToNow } from "date-fns";
import "./ScheduleDetail.css";

const API_URL =
  process.env.NODE_ENV === "production"
    ? process.env.REACT_APP_API_URL || window.location.origin
    : "http://localhost:6000";

const COLORS = ["#6366f1","#f59e0b","#10b981","#ef4444","#3b82f6","#8b5cf6"];

const getInitials = (name) =>
  name?.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2) || "?";

const UserChip = ({ u }) => {
  if (!u) return null;
  const imgSrc = u.avatar
    ? u.avatar.startsWith("http") ? u.avatar : `${API_URL}${u.avatar}`
    : null;
  const bg = COLORS[(u.name?.charCodeAt(0) || 0) % COLORS.length];
  return (
    <div style={{
      display: "inline-flex", alignItems: "center", gap: 7,
      background: "var(--surface2)", border: "1px solid var(--border)",
      borderRadius: 999, padding: "4px 12px 4px 4px",
    }}>
      <div style={{
        width: 26, height: 26, borderRadius: "50%", flexShrink: 0,
        background: imgSrc ? "transparent" : bg,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 10, fontWeight: 700, color: "#fff", overflow: "hidden",
      }}>
        {imgSrc
          ? <img src={imgSrc} alt={u.name} style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "50%" }} />
          : getInitials(u.name)
        }
      </div>
      <span style={{ fontSize: "0.85rem", fontWeight: 600, color: "var(--text)" }}>{u.name}</span>
      {u.designation && (
        <span style={{ fontSize: "0.72rem", color: "var(--text-muted)", borderLeft: "1px solid var(--border)", paddingLeft: 7 }}>
          {u.designation}
        </span>
      )}
    </div>
  );
};

const KvRow = ({ icon, label, children }) => (
  <div style={{
    display: "flex", alignItems: "flex-start", gap: 12,
    padding: "12px 0", borderBottom: "1px solid var(--border)",
  }}>
    <div style={{
      width: 32, height: 32, borderRadius: 8, flexShrink: 0,
      background: "rgba(99,102,241,0.10)", display: "flex",
      alignItems: "center", justifyContent: "center", color: "var(--primary)",
    }}>
      {icon}
    </div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: "0.72rem", fontWeight: 800, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: 4 }}>
        {label}
      </div>
      <div style={{ fontSize: "0.9rem", fontWeight: 600, color: "var(--text)" }}>
        {children}
      </div>
    </div>
  </div>
);

const ScheduleDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    api.get(`/scheduled-tasks/${id}`)
      .then(({ data }) => { if (mounted) setData(data.data); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [id]);

  if (loading) {
    return (
      <main className="page">
        <div className="loading"><div className="spinner" /></div>
      </main>
    );
  }

  if (!data) {
    return (
      <main className="page">
        <div className="empty-state">
          <h3>Schedule not found</h3>
          <button className="btn btn-primary" onClick={() => navigate(-1)}>Go back</button>
        </div>
      </main>
    );
  }

  // Normalise assignedTo → always array of user objects
  const assignees = Array.isArray(data.assignedTo)
    ? data.assignedTo.filter(Boolean)
    : data.assignedTo ? [data.assignedTo] : [];

  const nextRun = data.nextRunAt || data.scheduleDate;
  const isRecurring = !!data.isRecurring;

  return (
    <main className="page">

      {/* ── Hero Card ──────────────────────────────────────────── */}
      <div className="schedule-hero-card" style={{ marginBottom: 20 }}>
        <div className="schedule-hero-inner">
          {/* Back + actions row */}
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => navigate(-1)}
              style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
            >
              <MdArrowBack size={16} /> Back
            </button>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button className="btn btn-secondary btn-sm" onClick={() => navigate("/scheduled-tasks/all")}>
                All schedules
              </button>
              <button
                className="btn btn-primary btn-sm"
                disabled={!!data.executed && !isRecurring}
                onClick={() => navigate(`/scheduled-tasks/${data._id}/edit`)}
                title={data.executed && !isRecurring ? "Executed schedules cannot be edited" : "Edit schedule"}
                style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
              >
                <MdEdit size={15} /> Edit
              </button>
            </div>
          </div>

          {/* Title */}
          <h1 className="schedule-title" style={{ marginTop: 14 }}>
            <span className="icon-wrap">
              <MdSchedule color="var(--primary)" size={22} />
            </span>
            <span>{data.title}</span>
          </h1>

          {/* Badges */}
          <div className="schedule-badges" style={{ marginTop: 10 }}>
            <span className={`schedule-badge ${data.executed && !isRecurring ? "success" : "pending"}`}>
              {data.executed && !isRecurring
                ? <><MdCheckCircle size={14} /> Executed</>
                : <><MdAccessTime size={14} /> {isRecurring ? "Recurring" : "Upcoming"}</>
              }
            </span>
            <span className={`badge badge-${data.priority || "medium"}`} style={{ fontSize: "0.72rem" }}>
              <MdFlag size={13} /> {data.priority || "medium"}
            </span>
            {isRecurring && (
              <span className="schedule-badge" style={{ color: "var(--primary)", borderColor: "var(--primary)", background: "rgba(99,102,241,.08)" }}>
                <MdAutorenew size={14} /> Monthly recurring
              </span>
            )}
            {data.targetTask && (
              <span className="schedule-badge">
                <MdLink size={14} /> Task created
              </span>
            )}
            {assignees.length > 1 && (
              <span className="schedule-badge" style={{ color: "#0891b2", borderColor: "#bae6fd", background: "rgba(14,165,233,.08)" }}>
                <MdPeople size={14} /> {assignees.length} assignees
              </span>
            )}
          </div>

          {/* Subtitle */}
          <p className="schedule-subtitle" style={{ marginTop: 10 }}>
            {isRecurring
              ? `Runs every month on day ${data.recurrenceDayOfMonth || new Date(data.scheduleDate).getDate()}. Next run: ${nextRun ? format(new Date(nextRun), "PPp") : "—"}.`
              : `One-time schedule. ${data.executed ? "Task has been created." : `Assigns ${nextRun ? formatDistanceToNow(new Date(nextRun), { addSuffix: true }) : "soon"}.`}`
            }
          </p>
        </div>
      </div>

      {/* ── Body: two-column layout ───────────────────────────── */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 340px", gap: 16, alignItems: "start" }}>

        {/* Left column */}
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

          {/* Assignees card */}
          <div className="card card-body">
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
              {assignees.length === 1 ? <MdPerson size={18} color="var(--primary)" /> : <MdPeople size={18} color="var(--primary)" />}
              <span style={{ fontWeight: 800, fontSize: "0.9rem" }}>
                Assigned To{assignees.length > 1 ? ` (${assignees.length} users)` : ""}
              </span>
            </div>
            {assignees.length === 0 ? (
              <span style={{ color: "var(--text-muted)", fontSize: "0.88rem" }}>No assignees</span>
            ) : (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {assignees.map((u, i) => <UserChip key={u?._id || i} u={u} />)}
              </div>
            )}
          </div>

          {/* Details card */}
          <div className="card card-body">
            <div style={{ fontWeight: 800, fontSize: "0.9rem", marginBottom: 4 }}>Details</div>

            <KvRow icon={<MdInfo size={16} />} label="Description">
              <span style={{ whiteSpace: "pre-wrap", color: data.description ? "var(--text)" : "var(--text-muted)", fontWeight: data.description ? 600 : 400 }}>
                {data.description || "No description provided."}
              </span>
            </KvRow>

            <KvRow icon={<MdPerson size={16} />} label="Scheduled by">
              {data.createdBy?.name || "—"}
            </KvRow>

            <KvRow icon={<MdCalendarToday size={16} />} label="Assign On">
              {data.scheduleDate ? format(new Date(data.scheduleDate), "PPpp") : "—"}
            </KvRow>

            {isRecurring && (
              <KvRow icon={<MdAutorenew size={16} />} label="Next Run">
                {nextRun
                  ? <>{format(new Date(nextRun), "PPpp")} <span style={{ color: "var(--text-muted)", fontWeight: 400, fontSize: "0.8rem" }}>({formatDistanceToNow(new Date(nextRun), { addSuffix: true })})</span></>
                  : "—"
                }
              </KvRow>
            )}

            <KvRow icon={<MdEvent size={16} />} label="Task Deadline">
              {data.deadline ? format(new Date(data.deadline), "PP") : <span style={{ color: "var(--text-muted)", fontWeight: 400 }}>No deadline set</span>}
            </KvRow>

            <div style={{ borderBottom: "none" }}>
              <KvRow icon={<MdLabel size={16} />} label="Tags">
                {Array.isArray(data.tags) && data.tags.length ? (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 2 }}>
                    {data.tags.map((t) => (
                      <span key={t} className="schedule-tag">{t}</span>
                    ))}
                  </div>
                ) : <span style={{ color: "var(--text-muted)", fontWeight: 400 }}>No tags</span>}
              </KvRow>
            </div>
          </div>
        </div>

        {/* Right column — status panel */}
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div className="card card-body">
            <div style={{ fontWeight: 800, fontSize: "0.9rem", marginBottom: 12 }}>Overview</div>

            {[
              {
                icon: <MdFlag size={16} />, label: "Priority",
                value: <span className={`badge badge-${data.priority || "medium"}`}>{data.priority || "medium"}</span>,
              },
              {
                icon: <MdInfo size={16} />, label: "Status",
                value: (
                  <span className={`schedule-badge ${data.executed && !isRecurring ? "success" : "pending"}`} style={{ fontSize: "0.75rem" }}>
                    {data.executed && !isRecurring ? "Executed" : isRecurring ? "Active recurring" : "Upcoming"}
                  </span>
                ),
              },
              {
                icon: <MdAutorenew size={16} />, label: "Recurrence",
                value: isRecurring
                  ? <span style={{ color: "var(--primary)", fontWeight: 700 }}>Monthly (day {data.recurrenceDayOfMonth})</span>
                  : <span style={{ color: "var(--text-muted)", fontWeight: 400 }}>One-time</span>,
              },
              {
                icon: <MdPeople size={16} />, label: "Assignees",
                value: <span style={{ fontWeight: 700 }}>{assignees.length || "—"}</span>,
              },
              {
                icon: <MdCalendarToday size={16} />, label: "Created",
                value: data.createdAt
                  ? <span title={format(new Date(data.createdAt), "PPpp")}>{formatDistanceToNow(new Date(data.createdAt), { addSuffix: true })}</span>
                  : "—",
              },
              {
                icon: <MdAccessTime size={16} />, label: "Last run",
                value: data.lastRunAt
                  ? <span title={format(new Date(data.lastRunAt), "PPpp")}>{formatDistanceToNow(new Date(data.lastRunAt), { addSuffix: true })}</span>
                  : <span style={{ color: "var(--text-muted)", fontWeight: 400 }}>Not yet</span>,
              },
            ].map(({ icon, label, value }) => (
              <div key={label} style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                gap: 10, padding: "10px 0", borderBottom: "1px solid var(--border)",
              }}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "var(--text-muted)", fontWeight: 700, fontSize: "0.82rem" }}>
                  <span style={{ color: "var(--primary)" }}>{icon}</span>
                  {label}
                </span>
                <span style={{ fontSize: "0.84rem", textAlign: "right" }}>{value}</span>
              </div>
            ))}
          </div>

          {/* Linked task */}
          {data.targetTask && (
            <div className="card card-body" style={{ background: "rgba(16,185,129,.05)", border: "1px solid rgba(16,185,129,.2)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                <MdLink size={16} color="#059669" />
                <span style={{ fontWeight: 800, fontSize: "0.88rem", color: "#059669" }}>Linked Task</span>
              </div>
              <p style={{ fontSize: "0.82rem", color: "var(--text-muted)", margin: 0 }}>
                A task was created from this schedule.
              </p>
            </div>
          )}
        </div>
      </div>

    </main>
  );
};

export default ScheduleDetail;
